import React, { useState } from 'react';
import axios from 'axios';
import { ReactComponent as Loader } from '../../assets/img/loader.svg';

export default function Modal({ modal, setModal }) {
    const countries = [
        { name: 'Nigeria', code: 'NG' },
        { name: 'Ghana', code: 'GH' },
        { name: 'Kenya', code: 'KE' },
        { name: 'South Africa', code: 'ZA' },
        { name: 'United Kingdom', code: 'GB' },
        { name: 'United States', code: 'US' },
    ];

    const [error, setError] = useState("none");
    const [url, setUrl] = useState("");
    const [state, setState] = useState({
        country: "Nigeria",
        name: "",
        address: "",
        email: "",
        phone: "",
        nafdac: "",
        logo: null,
        logoUrl: ""
    });

    const [loader, setLoader] = useState(false);

    const changeHandler = (e) => {
        const { name, value, files } = e.target;
        if (name === "logo") {
            setState({
                ...state,
                logo: files[0],
                logoUrl: files[0].name
            });
        } else {
            setState({
                ...state,
                [name]: value
            });
        }
    };

    const pushDetails = async (imageUrl = "") => {
        setLoader(true);
        try {
            await axios.post(process.env.REACT_APP_SHEET_BEST_URL, {
                country: state.country,
                name: state.name,
                address: state.address,
                email: state.email,
                phone: state.phone,
                nafdac: state.nafdac,
                logoUrl: imageUrl
            });
            setLoader(false);
            setError("success");
        } catch (err) {
            console.error(err);
            setError("error");
            setLoader(false);
        }
    };

    const uploadImage = async () => {
        setLoader(true);
        const data = new FormData();
        data.append("file", state.logo);
        data.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
        data.append("cloud_name", process.env.REACT_APP_CLOUDINARY_CLOUD_NAME);
        data.append("name", `${state.name}`);

        try {
            const resp = await fetch(`https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`, {
                method: "post",
                body: data
            });
            const imgData = await resp.json();
            setUrl(imgData.url);
            await pushDetails(imgData.url);
        } catch (err) {
            console.error(err);
            setError("error");
            setLoader(false);
        }
    };

    const submitForm = async (e) => {
        e.preventDefault();
        if (!state.country || !state.name || !state.address || !state.email || !state.phone || !state.nafdac || !state.logo) {
            setError("incomplete");
            return;
        }
        setError("none");
        await uploadImage();
    };

    if (!modal) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-300" 
                onClick={() => { setError("none"); setModal(false); }}
            />
            
            {/* Modal Container */}
            <div className="relative w-full max-w-xl bg-white rounded-[2.5rem] shadow-clinical overflow-y-auto max-h-[90vh] animate-in zoom-in-95 slide-in-from-bottom-10 duration-500">
                <div className="p-6 md:p-14">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-8">
                        <div className="space-y-2">
                            <h2 className="text-3xl font-headline font-extrabold text-slate-900 tracking-tight">Partner Enrollment</h2>
                            <p className="text-slate-500 text-sm leading-relaxed">Systematize your distribution with Pairwota.</p>
                        </div>
                        <button 
                            onClick={() => { setError("none"); setModal(false); }}
                            className="p-3 bg-slate-50 rounded-2xl text-slate-400 hover:text-primary hover:bg-blue-50 transition-all group"
                        >
                            <span className="material-symbols-outlined group-hover:rotate-90 transition-transform">close</span>
                        </button>
                    </div>

                    {/* Feedback Messages */}
                    {error !== "none" && (
                        <div className={`mb-8 p-6 rounded-2xl flex items-center justify-between gap-4 animate-in fade-in slide-in-from-top-4 duration-300 ${
                            error === "success" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-rose-50 text-rose-700 border border-rose-100"
                        }`}>
                            <div className="flex items-center gap-3">
                                <span className="material-symbols-outlined">
                                    {error === "success" ? "check_circle" : "error"}
                                </span>
                                <span className="text-sm font-bold">
                                    {error === "success" && "Enrollment Successful. Our team will verify your credentials."}
                                    {error === "error" && "Protocol failure. Please verify your connection."}
                                    {error === "incomplete" && "Clinical Error: All fields are mandatory."}
                                </span>
                            </div>
                            <button onClick={() => setError("none")} className="text-current opacity-50 hover:opacity-100 transition-opacity">
                                <span className="material-symbols-outlined text-sm">close</span>
                            </button>
                        </div>
                    )}

                    <form onSubmit={submitForm} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Country Select */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 pl-1">Clinical Radius</label>
                                <select 
                                    name="country" 
                                    value={state.country} 
                                    onChange={changeHandler}
                                    className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-900 focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all outline-none appearance-none font-medium"
                                >
                                    {countries.map(c => <option key={c.code} value={c.name}>{c.name}</option>)}
                                </select>
                            </div>

                            {/* Company Name */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 pl-1">Entity Name</label>
                                <input 
                                    required 
                                    name="name"
                                    value={state.name}
                                    onChange={changeHandler}
                                    placeholder="BlueWave Water Ltd."
                                    className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-900 focus:ring-4 focus:ring-primary/10 transition-all outline-none"
                                />
                            </div>
                        </div>

                        {/* Address */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 pl-1">Operational Headquarter</label>
                            <input 
                                required 
                                name="address"
                                value={state.address}
                                onChange={changeHandler}
                                placeholder="Victoria Island, Lagos, Nigeria"
                                className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-900 focus:ring-4 focus:ring-primary/10 transition-all outline-none"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Email */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 pl-1">Secure Email</label>
                                <input 
                                    required 
                                    type="email"
                                    name="email"
                                    value={state.email}
                                    onChange={changeHandler}
                                    placeholder="contact@entity.com"
                                    className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-900 focus:ring-4 focus:ring-primary/10 transition-all outline-none"
                                />
                            </div>

                            {/* Phone */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 pl-1">Telemetry Number</label>
                                <input 
                                    required 
                                    name="phone"
                                    value={state.phone}
                                    onChange={changeHandler}
                                    placeholder="+234 000 000 0000"
                                    className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-900 focus:ring-4 focus:ring-primary/10 transition-all outline-none"
                                />
                            </div>
                        </div>

                        {/* NAFDAC */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 pl-1">NAFDAC Compliance Identifier</label>
                            <input 
                                required 
                                name="nafdac"
                                value={state.nafdac}
                                onChange={changeHandler}
                                placeholder="B1-XXXXXXX"
                                className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-900 focus:ring-4 focus:ring-primary/10 transition-all outline-none uppercase"
                            />
                        </div>

                        {/* Logo Upload */}
                        <div className="space-y-3 pt-2">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 pl-1 flex items-center justify-between">
                                Visual Identity Symbol
                                {state.logoUrl && <span className="text-primary font-bold lowercase">{state.logoUrl}</span>}
                            </label>
                            <div className="relative group">
                                <input 
                                    id="logo-upload"
                                    type="file" 
                                    name="logo"
                                    onChange={changeHandler}
                                    className="hidden"
                                    accept="image/*"
                                />
                                <label 
                                    htmlFor="logo-upload"
                                    className="flex items-center justify-between p-4 px-6 bg-white border-2 border-dashed border-slate-200 rounded-2xl cursor-pointer hover:border-primary/50 hover:bg-blue-50/50 transition-all"
                                >
                                    <div className="flex items-center gap-4 text-slate-500">
                                        <span className="material-symbols-outlined text-3xl">image</span>
                                        <span className="text-sm font-bold">Select entity logo</span>
                                    </div>
                                    <span className="material-symbols-outlined text-slate-400 uppercase text-sm">upload_file</span>
                                </label>
                            </div>
                        </div>

                        {/* Footer Buttons */}
                        <div className="flex gap-4 pt-6">
                            <button 
                                type="submit" 
                                disabled={loader}
                                className={`flex-1 flex items-center justify-center gap-3 py-5 rounded-2xl font-bold text-lg transition-all active:scale-95 shadow-lg shadow-primary/20 ${
                                    loader ? "bg-blue-300 cursor-not-allowed" : "bg-primary text-white hover:bg-blue-700"
                                }`}
                            >
                                {loader ? <Loader className="w-6 h-6 animate-spin" /> : "Initiate Verification"}
                                {!loader && <span className="material-symbols-outlined">rocket_launch</span>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
